import { type CollabManager } from '@databases/collab-state-manager/collab-manager';
import { DATABASE_BE_STREAMING_PARTICIPANT } from '@databases/database-ai-first-creation-state';
import { createParticipantFromIdentity, type Participant } from '@databases/presence';

import { IdentityKind } from '@canvas/model/identity';
import { BackendAgentOrigin, type Transaction } from '@canvas/transaction';

/** Keep the streaming presence identity separate from real awareness participants. */
export function getBackendAgentStreamingParticipant(
  transaction: Transaction,
  collabManager: CollabManager
): Participant {
  const ownerAccountId =
    transaction.origin instanceof BackendAgentOrigin
      ? transaction.origin.agentOwnerAccountId
      : undefined;
  if (!ownerAccountId) {
    return DATABASE_BE_STREAMING_PARTICIPANT;
  }

  const matchingAgentClientIds = Object.entries(collabManager.participants ?? {}).flatMap(
    ([clientId, identity]) => {
      if (
        identity.type !== IdentityKind.AiAgent ||
        !('agentOwnerAccountId' in identity) ||
        identity.agentOwnerAccountId !== ownerAccountId
      ) {
        return [];
      }

      const numericClientId = Number(clientId);
      return Number.isSafeInteger(numericClientId) &&
        numericClientId >= 0 &&
        String(numericClientId) === clientId
        ? [numericClientId]
        : [];
    }
  );
  // Awareness can arrive after the transaction or disappear before the reveal.
  // The owner keeps the fallback stable; the finite palette cannot ensure unique colors.
  const ownerColorSeed = [...ownerAccountId].reduce(
    (hash, character) => (Math.imul(hash, 31) + character.charCodeAt(0)) >>> 0,
    0
  );
  const colorSeed =
    matchingAgentClientIds.length === 1 ? matchingAgentClientIds[0] : ownerColorSeed;
  return {
    identity: {
      ...DATABASE_BE_STREAMING_PARTICIPANT.identity,
      accountId: `${DATABASE_BE_STREAMING_PARTICIPANT.identity.accountId}:${ownerAccountId}`
    },
    colorToken: createParticipantFromIdentity(
      DATABASE_BE_STREAMING_PARTICIPANT.identity,
      colorSeed
    ).colorToken
  };
}
